/**
 * 棋盘输入：单击 / 双击 / 滑动涂画。用 Pointer Events 统一鼠标与触摸。
 *
 * 手势判定与 App 端一致：
 *   单击**立即**提交（零延迟），不等双击窗口 —— 等待会让点击感觉发黏。
 *   同格在窗口内的第二下判为双击，且不再补发单击。
 *   因为白叉是可移除态，「单击落白叉 → 双击升级为星」的净效果与直接双击相同。
 *
 * 点按与涂画的区分：按下时只记录起点；只有指针移动到**另一格**才转为涂画，
 * 抬起时若从未跨格则算点按。这样点按不会误触发涂画。
 */

const DOUBLE_CLICK_MS = 250;

export class BoardInput {
    /**
     * @param {import('./boardView.js').BoardView} view
     * @param {object} [opts]
     * @param {number} [opts.doubleClickMs]
     */
    constructor(view, opts = {}) {
        this.view = view;
        this.doubleClickMs = opts.doubleClickMs ?? DOUBLE_CLICK_MS;

        /** @type {import('../core/session.js').GameSession | null} */
        this.session = null;
        /** 是否接受输入（动画播放期间关掉）。 */
        this.enabled = true;

        // 双击判定的待定状态
        this._pending = null; // { r, c, t }

        // 本次指针交互
        this._down = null;    // { r, c, pointerId }
        this._painting = false;

        this._onDown = this._handleDown.bind(this);
        this._onMove = this._handleMove.bind(this);
        this._onUp = this._handleUp.bind(this);
        this._onCancel = this._handleCancel.bind(this);
        this._bound = false;
    }

    /** @param {import('../core/session.js').GameSession} session */
    attach(session) {
        this.session = session;
        this._pending = null;
        this._down = null;
        this._painting = false;

        if (this._bound) return;
        const el = this.view.root;
        el.addEventListener('pointerdown', this._onDown);
        el.addEventListener('pointermove', this._onMove);
        el.addEventListener('pointerup', this._onUp);
        el.addEventListener('pointercancel', this._onCancel);
        // 长按不要弹出系统菜单，会打断涂画
        el.addEventListener('contextmenu', e => e.preventDefault());
        this._bound = true;
    }

    detach() {
        if (!this._bound) return;
        const el = this.view.root;
        el.removeEventListener('pointerdown', this._onDown);
        el.removeEventListener('pointermove', this._onMove);
        el.removeEventListener('pointerup', this._onUp);
        el.removeEventListener('pointercancel', this._onCancel);
        this._bound = false;
        this.session = null;
    }

    /** @param {PointerEvent} e */
    _handleDown(e) {
        if (!this.enabled || !this.session || this.session.isOver) return;
        const hit = this.view.cellFromPoint(e.clientX, e.clientY);
        if (!hit) return;

        e.preventDefault();
        // 捕获指针：手指滑出棋盘再滑回来也不会丢事件
        try {
            this.view.root.setPointerCapture(e.pointerId);
        } catch {
            /* 某些浏览器在合成事件上会抛，忽略 */
        }

        this._down = { r: hit.r, c: hit.c, pointerId: e.pointerId };
        this._painting = false;
    }

    /** @param {PointerEvent} e */
    _handleMove(e) {
        if (!this.enabled || !this.session || !this._down) return;
        if (e.pointerId !== this._down.pointerId) return;

        // 松手事件不保证送达：拖出窗口后松开、长按被原生拖拽接管、系统弹窗抢焦点、
        // 捕获中的元素被移除……任何一种都会让 pointerup / pointercancel 落空。
        // 那样 _down 会一直留着，之后鼠标空手划过棋盘就继续画叉（幽灵涂画）。
        //
        // 不去逐条堵这些路径 —— 浏览器丢事件的方式列不完。移动事件本身带着当前
        // 按键位掩码，按它兜底：为 0 说明早就松开了，就地收尾，下一次移动即自愈。
        //
        // 只对鼠标生效：触摸的 pointermove 在按住时 buttons 未必是 1，
        // 照搬会把手机上的滑动涂画直接掐断。
        if (e.pointerType === 'mouse' && e.buttons === 0) {
            this._finish();
            return;
        }

        const hit = this.view.cellFromPoint(e.clientX, e.clientY);
        if (!hit) return;
        if (hit.r === this._down.r && hit.c === this._down.c) return; // 还在起点格

        e.preventDefault();
        if (!this._painting) {
            // 首次跨格 → 转为涂画，从起点格开始
            this._painting = true;
            this._pending = null; // 涂画取消双击待定
            this.session.beginPaint(this._down.r, this._down.c);
        }
        this.session.paintAt(hit.r, hit.c);
    }

    /** @param {PointerEvent} e */
    _handleUp(e) {
        if (!this.session || !this._down) return;
        if (e.pointerId !== this._down.pointerId) return;

        try {
            this.view.root.releasePointerCapture(e.pointerId);
        } catch { /* 同上 */ }

        if (this._painting) {
            this.session.endPaint();
        } else if (this.enabled && !this.session.isOver) {
            this._click(this._down.r, this._down.c);
        }
        this._down = null;
        this._painting = false;
    }

    _handleCancel() {
        this._finish();
    }

    /**
     * 收尾并清空本次交互，不产生点击。
     * 用于交互被中断（pointercancel）或发现松手事件已丢失的场合。
     * `endPaint()` 是幂等的，重复调用无副作用。
     */
    _finish() {
        if (this.session && this._painting) this.session.endPaint();
        this._down = null;
        this._painting = false;
    }

    /**
     * 点按 → 单击或双击。
     * @param {number} r @param {number} c
     */
    _click(r, c) {
        const now = performance.now();
        const p = this._pending;

        if (p && p.r === r && p.c === c && now - p.t <= this.doubleClickMs) {
            this._pending = null;
            this.session.applyGesture(r, c, 'double');
            return;
        }

        this.session.applyGesture(r, c, 'single');
        this._pending = { r, c, t: now };
    }
}
